import React from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { IButtonProps } from '@rbi-ctg/frontend';
import { ServiceMode } from 'state/order';
import { useStoreContext } from 'state/store';

import { ModalContent, ModalHeading } from './styled';
import { OnServiceModeSelected } from './types';

interface IModalContentProps {
  selectServiceMode: OnServiceModeSelected;
}

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  margin-top: 2.3rem;
`;

const ModeColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

const StyledButton = styled.button`
  width: 100%;
  border: 0;
  border-bottom: 1px solid ${Styles.color.grey.four};
  padding-left: 1rem;
  font-family: ${Styles.fontFamily.base};

  background: transparent;
  color: ${Styles.color.secondary};

  &:hover {
    color: ${Styles.color.grey.one};
    background-color: ${Styles.color.grey.five};
  }

  cursor: pointer;

  &[disabled] {
    color: ${Styles.color.grey.two};
    cursor: not-allowed;
  }
`;

const WrapperButton: React.FC<IButtonProps> = ({
  children,
  disabled,
  onClick,
  'data-testid': dataQaId,
}) => (
  <StyledButton data-testid={dataQaId} onClick={onClick} disabled={disabled}>
    {children}
  </StyledButton>
);

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  margin-right: 14px;
  margin-top: 2px;

  ${Styles.desktop`
    margin-right: 18px;
  `}
`;

const TextRow = styled.div<{ isTitle?: boolean }>`
  width: 100%;
  text-align: start;
  margin: 0;
  padding: 0.625rem 0 0.75rem;
  ${p =>
    p.isTitle &&
    css`
      padding: 0 0 1rem;
      border-bottom: 1px solid ${Styles.color.grey.four};
    `};
`;

const Title = styled.span`
  font-size: 1.5rem;
  line-height: 1.2;
  color: ${Styles.color.black};
  text-transform: uppercase;
  font-family: ${Styles.fontFamily.base};
  font-weight: ${Styles.fontWeight.normal};
  letter-spacing: 'normal';
`;

const Mode = styled.span`
  font-size: 0.9375rem;
`;

export const PickupMode: React.FC<{
  serviceMode: ServiceMode;
  modeAvailable: boolean;
  availableText: string;
  unavailableText: string;
  onSelected: OnServiceModeSelected;
  testId: string;
}> = ({ serviceMode, modeAvailable, availableText, unavailableText, onSelected, testId }) => (
  <WrapperButton
    disabled={!modeAvailable}
    data-testid={testId}
    onClick={() => onSelected(serviceMode)}
  >
    <TextRow>
      <Mode>{modeAvailable ? availableText : unavailableText}</Mode>
    </TextRow>
  </WrapperButton>
);

const PickupModeContent: React.FC<IModalContentProps> = props => {
  const { serviceModeStatus } = useStoreContext();
  const { formatMessage } = useIntl();

  const driveThruAvailable = serviceModeStatus.DRIVE_THRU.available;
  const takeOutAvailable = serviceModeStatus.TAKEOUT.available;
  const dineInAvailable = serviceModeStatus.EAT_IN.available;
  const curbsideAvailable = serviceModeStatus.CURBSIDE.available;

  const driveThruDisabled = serviceModeStatus.DRIVE_THRU.disabled;
  const takeOutDisabled = serviceModeStatus.TAKEOUT.disabled;
  const dineInDisabled = serviceModeStatus.EAT_IN.disabled;
  const disableCurbside = serviceModeStatus.CURBSIDE.disabled;

  const hasPickupOptionsAvailable = [driveThruAvailable, takeOutAvailable, curbsideAvailable].some(
    Boolean
  );
  const hasDineInOptionsAvailable = [dineInAvailable].some(Boolean);

  return (
    <ModalContent>
      <ModalHeading id="modal-heading" className="modal-heading">
        {formatMessage({ id: 'willPickUpOrder' })}
      </ModalHeading>
      {hasPickupOptionsAvailable && (
        <Section>
          <IconWrapper>
            <Icon icon="cart" color="icon-active" width="21px" height="21px" aria-hidden />
          </IconWrapper>
          <ModeColumn>
            <TextRow isTitle>
              <Title>{formatMessage({ id: 'pickUp' })}</Title>
            </TextRow>
            {driveThruAvailable && (
              <PickupMode
                serviceMode={ServiceMode.DRIVE_THRU}
                modeAvailable={!driveThruDisabled}
                availableText={formatMessage({ id: 'driveThru' })}
                unavailableText={formatMessage({ id: 'driveThruUnavailable' })}
                onSelected={props.selectServiceMode}
                testId="pickupmode-drive-thru"
              />
            )}
            {takeOutAvailable && (
              <PickupMode
                serviceMode={ServiceMode.TAKEOUT}
                modeAvailable={!takeOutDisabled}
                availableText={formatMessage({ id: 'takeOut' })}
                unavailableText={formatMessage({ id: 'takeOutUnavailable' })}
                onSelected={props.selectServiceMode}
                testId="pickupmode-take-out"
              />
            )}
            {curbsideAvailable && (
              <PickupMode
                serviceMode={ServiceMode.CURBSIDE}
                modeAvailable={!disableCurbside}
                availableText={formatMessage({ id: 'curbside' })}
                unavailableText={formatMessage({ id: 'curbsideUnavailable' })}
                onSelected={props.selectServiceMode}
                testId="pickupmode-curbside"
              />
            )}
          </ModeColumn>
        </Section>
      )}
      {hasDineInOptionsAvailable && (
        <Section>
          <IconWrapper>
            <Icon icon="restaurant" color="icon-active" width="21px" height="21px" aria-hidden />
          </IconWrapper>
          <ModeColumn>
            <TextRow isTitle>
              <Title>{formatMessage({ id: 'eatIn' })}</Title>
            </TextRow>
            {dineInAvailable && (
              <PickupMode
                serviceMode={ServiceMode.EAT_IN}
                modeAvailable={!dineInDisabled}
                availableText={formatMessage({ id: 'dineIn' })}
                unavailableText={formatMessage({ id: 'dineInUnavailable' })}
                onSelected={props.selectServiceMode}
                testId="pickupmode-dine-in"
              />
            )}
          </ModeColumn>
        </Section>
      )}
    </ModalContent>
  );
};

export default PickupModeContent;
